/**
 * The person's notification preferences (#1095), read from and written to the
 * user settings (`notification_preferences`).
 *
 * Writes go through `SettingsContext.saveSettings`, which PATCHes only the
 * changed top-level key and refuses to write before the settings loaded
 * (#908) — so `canEdit` is false until then, and the switches are disabled.
 * Without a SettingsProvider everything is on and nothing can be changed.
 */
import { useCallback, useMemo, useRef } from 'react'
import { useSettingsContextOptional } from '@/contexts/SettingsContext'
import type { NotificationCategory } from '@/services/notification-service'
import {
  parseNotificationPreferences,
  withPreference,
  type NotificationChannel,
  type NotificationPreferences,
} from '@/lib/utils/notificationPreferences'

export interface UseNotificationPreferencesReturn {
  preferences: NotificationPreferences
  canEdit: boolean
  /** Resolves false when the setting was not saved (the stored value stays). */
  setPreference: (category: NotificationCategory, channel: NotificationChannel, enabled: boolean) => Promise<boolean>
}

export function useNotificationPreferences(): UseNotificationPreferencesReturn {
  const context = useSettingsContextOptional()
  const contextRef = useRef(context)
  contextRef.current = context

  const raw = context?.settings.notification_preferences
  const preferences = useMemo(() => parseNotificationPreferences(raw), [raw])
  const canEdit = Boolean(context?.hasLoaded)

  const setPreference = useCallback(
    async (category: NotificationCategory, channel: NotificationChannel, enabled: boolean) => {
      const current = contextRef.current
      if (!current?.hasLoaded) return false
      const next = withPreference(
        parseNotificationPreferences(current.settings.notification_preferences),
        category,
        channel,
        enabled,
      )
      return current.saveSettings({ ...current.settings, notification_preferences: next })
    },
    [],
  )

  return { preferences, canEdit, setPreference }
}
